import { createRouter as createTanStackRouter } from "@tanstack/react-router";
import { scrubSentryBreadcrumb, scrubSentryEvent } from "./shared/analytics/scrub-url";
import { routerWithQueryClient } from "@tanstack/react-router-with-query";
import { isApiError } from "@majourneys/api-client";
import { routeTree } from "./routeTree.gen";
import { createQueryClient } from "./lib/query-client";
import { ErrorFallback } from "./shared/ui/error-fallback";
import { NotFound } from "./shared/ui/not-found";

const SENTRY_DSN =
  "https://b8e252ea4e1ecd4145f0fc62c4494ce0@o4511411771604992.ingest.de.sentry.io/4511411774095440";

let sentryScheduled = false;
/**
 * Sentry is ~80 KB of the main script and nothing on the page waits for it.
 * Load it once the browser is idle (or after 4 s at most) so the first
 * interaction is not competing with the error reporter's parse time. Errors
 * thrown before it arrives are not reported; accepted.
 */
function scheduleSentry() {
  if (sentryScheduled || typeof window === "undefined") return;
  sentryScheduled = true;
  const start = () => {
    void import("@sentry/react")
      .then((Sentry) => {
        Sentry.init({
          dsn: SENTRY_DSN,
          // No IPs, cookies or contact details: error reports are not the
          // place for a guest's personal data (see scrub-url.ts).
          sendDefaultPii: false,
          beforeSend: (event) => scrubSentryEvent(event),
          beforeBreadcrumb: (crumb) => scrubSentryBreadcrumb(crumb),
          integrations: [
            // send console.log, console.warn, and console.error calls as logs to Sentry
            Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
          ],
          // Enable logs to be sent to Sentry
          enableLogs: true,
        });
      })
      .catch(() => {
        /* the reporter failing to load must never take the page down */
      });
  };
  // Safari still lacks requestIdleCallback; a plain timer is the fallback.
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(start, { timeout: 4000 });
  } else {
    window.setTimeout(start, 2000);
  }
}

export function getRouter() {
  scheduleSentry();
  const queryClient = createQueryClient();
  const router = createTanStackRouter({
    routeTree,
    context: { queryClient },
    scrollRestoration: true,
    // A new page lands at the top (or at its saved place on Back) at once.
    // Left unset, the router's scrollTo picked up `scroll-behavior: smooth`
    // from lib/tailwind.css: the new page showed at the old scroll position,
    // then glided ~800ms to the top, and a click mid-glide left it halfway
    // (audit, 2026-11-02). In-page anchors keep the smooth scroll.
    scrollRestorationBehavior: "instant",
    defaultPreload: "intent",
    defaultErrorComponent: ({ error }) => {
      if (isApiError(error) && error.status === 404) return <NotFound />;
      return <ErrorFallback error={error as Error} />;
    },
    defaultNotFoundComponent: () => <NotFound />,
  });
  return routerWithQueryClient(router, queryClient);
}
